// src/components/WhatIDoSection.tsx
//
// Home page "Engineering capabilities".
//
// Replaces a generic four-item services list with the six areas he can
// actually be responsible for on a project. Backend & API Engineering is
// flagged in the config as the primary specialism so the list never reads as
// six equal offerings.
//
// The two viewports need genuinely different interaction, so only one is
// rendered at a time rather than both being hidden with CSS:
//
//   >= 1024px  a vertical index driving one detail panel (hover or focus to
//              select, arrow keys to move, Home/End to jump)
//   <  1024px  a stacked accordion
//
// No skill meters, percentage ratings, or icons: the claim is about
// responsibility, not a score.
import { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { homeConfig } from '../config/homeConfig';
import type { HomeCapabilityItem } from '../config/homeConfig';
import { minDevice } from '../styles/breakpoints';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { DURATION, EASE_OUT, STAGGER, motionSet } from './about/motion';
import {
  CapabilitiesRoot,
  CapabilitiesContainer,
  Header,
  Eyebrow,
  Heading,
  Lede,
  SplitLayout,
  IndexList,
  IndexButton,
  IndexNumber,
  IndexTitle,
  PrimaryTag,
  DetailPanel,
  DetailIndex,
  DetailTitle,
  DetailSummary,
  DetailMeta,
  MetaLabel,
  MetaRun,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionChevron,
  AccordionBody,
  AccordionInner,
} from './WhatIDoSection.styles';

const headingId = 'home-capabilities-heading';

// One stable id for the single detail panel, so every tab's aria-controls
// resolves. A per-tab panel id would dangle for the five inactive tabs.
const panelId = 'home-capability-panel';

const viewport = { once: true, amount: 0.3 } as const;

type Labels = typeof homeConfig.capabilities;

/** The detail body, shared by the panel and the accordion so they cannot drift. */
const DetailContent = ({ item, labels }: { item: HomeCapabilityItem; labels: Labels }) => (
  <>
    <DetailIndex>{item.index}</DetailIndex>
    <DetailTitle>{item.title}</DetailTitle>
    <DetailSummary>{item.summary}</DetailSummary>
    <DetailMeta>
      <div>
        <MetaLabel>{labels.focusLabel}</MetaLabel>
        <MetaRun>
          {item.focus.map((entry) => (
            <span key={entry}>{entry}</span>
          ))}
        </MetaRun>
      </div>
      <div>
        <MetaLabel>{labels.stackLabel}</MetaLabel>
        <MetaRun>
          {item.stack.map((entry) => (
            <span key={entry}>{entry}</span>
          ))}
        </MetaRun>
      </div>
    </DetailMeta>
  </>
);

const WhatIDoSection = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const isDesktop = useMediaQuery(minDevice.laptop);

  const labels = homeConfig.capabilities;
  const { items } = labels;
  const anim = motionSet(reduceMotion);

  const primaryId = items.find((item) => item.primary)?.id ?? items[0].id;
  const [activeId, setActiveId] = useState(primaryId);
  const [openId, setOpenId] = useState<string | null>(primaryId);

  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const active = items.find((item) => item.id === activeId) ?? items[0];

  const select = useCallback((id: string) => setActiveId(id), []);

  const focusTab = (index: number) => {
    const target = tabRefs.current[index];
    if (target) target.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        select(items[(index + 1) % items.length].id);
        focusTab((index + 1) % items.length);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        select(items[(index - 1 + items.length) % items.length].id);
        focusTab((index - 1 + items.length) % items.length);
        break;
      case 'Home':
        event.preventDefault();
        select(items[0].id);
        focusTab(0);
        break;
      case 'End':
        event.preventDefault();
        select(items[items.length - 1].id);
        focusTab(items.length - 1);
        break;
      default:
        break;
    }
  };

  // Accordion collapse. Height is animated because the body is in normal flow
  // rather than overlaid; reduced motion gets an instant swap.
  const accordionMotion = reduceMotion
    ? {
        initial: { height: 0 as const, opacity: 0 },
        animate: { height: 'auto' as const, opacity: 1 },
        exit: { height: 0 as const, opacity: 0 },
        transition: { duration: 0 },
      }
    : {
        initial: { height: 0 as const, opacity: 0 },
        animate: { height: 'auto' as const, opacity: 1 },
        exit: { height: 0 as const, opacity: 0 },
        transition: { duration: DURATION.panelIn, ease: EASE_OUT },
      };

  return (
    <CapabilitiesRoot aria-labelledby={headingId}>
      <CapabilitiesContainer>
        <Header variants={anim.group(STAGGER.group)} initial="hidden" whileInView="visible" viewport={viewport}>
          <Eyebrow variants={anim.reveal}>{labels.eyebrow}</Eyebrow>
          <Heading id={headingId} variants={anim.reveal}>
            {labels.heading}
          </Heading>
          <Lede variants={anim.reveal}>{labels.lede}</Lede>
        </Header>

        {isDesktop ? (
          <SplitLayout>
            <IndexList
              role="tablist"
              aria-orientation="vertical"
              aria-label={labels.listLabel}
            >
              {items.map((item, index) => {
                const $active = item.id === active.id;

                return (
                  <IndexButton
                    key={item.id}
                    ref={(node) => {
                      tabRefs.current[index] = node;
                    }}
                    type="button"
                    role="tab"
                    id={`capability-tab-${item.id}`}
                    aria-selected={$active}
                    aria-controls={panelId}
                    tabIndex={$active ? 0 : -1}
                    $active={$active}
                    onClick={() => select(item.id)}
                    onMouseEnter={() => select(item.id)}
                    onFocus={() => select(item.id)}
                    onKeyDown={(event) => handleKeyDown(event, index)}
                  >
                    <IndexNumber $active={$active} aria-hidden="true">
                      {item.index}
                    </IndexNumber>
                    <IndexTitle>
                      {item.title}
                      {item.primary && <PrimaryTag>{labels.primaryLabel}</PrimaryTag>}
                    </IndexTitle>
                  </IndexButton>
                );
              })}
            </IndexList>

            <DetailPanel
              role="tabpanel"
              id={panelId}
              aria-labelledby={`capability-tab-${active.id}`}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={active.id}
                  variants={anim.panel}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <DetailContent item={active} labels={labels} />
                </motion.div>
              </AnimatePresence>
            </DetailPanel>
          </SplitLayout>
        ) : (
          <Accordion>
            {items.map((item) => {
              const isOpen = openId === item.id;

              return (
                <AccordionItem key={item.id}>
                  <AccordionButton
                    type="button"
                    id={`capability-accordion-${item.id}`}
                    aria-expanded={isOpen}
                    aria-controls={`capability-accordion-body-${item.id}`}
                    $open={isOpen}
                    onClick={() => setOpenId(isOpen ? null : item.id)}
                  >
                    <IndexNumber $active={isOpen} aria-hidden="true">
                      {item.index}
                    </IndexNumber>
                    <IndexTitle>
                      {item.title}
                      {item.primary && <PrimaryTag>{labels.primaryLabel}</PrimaryTag>}
                    </IndexTitle>
                    <AccordionChevron
                      aria-hidden="true"
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={
                        reduceMotion
                          ? { duration: 0 }
                          : { duration: DURATION.micro, ease: EASE_OUT }
                      }
                    >
                      ▾
                    </AccordionChevron>
                  </AccordionButton>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <AccordionBody
                        id={`capability-accordion-body-${item.id}`}
                        role="region"
                        aria-labelledby={`capability-accordion-${item.id}`}
                        initial={accordionMotion.initial}
                        animate={accordionMotion.animate}
                        exit={accordionMotion.exit}
                        transition={accordionMotion.transition}
                      >
                        <AccordionInner>
                          <DetailContent item={item} labels={labels} />
                        </AccordionInner>
                      </AccordionBody>
                    )}
                  </AnimatePresence>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </CapabilitiesContainer>
    </CapabilitiesRoot>
  );
};

export default WhatIDoSection;
